<template>
    <Modal ref="modalRef" :processing="form.processing" :show-primary-button="false" max-width="md">
        <template #title>{{ trans('profile.sms_notifications.header') }}</template>

        <p class="mb-6 text-gray-600">
            {{ trans('profile.sms_notifications.verification.description', { phoneNumber: phoneNumber }) }}
        </p>

        <div class="flex flex-col items-center">
            <PinInput ref="pinInputRef" v-model="codeDigits" :disabled="form.processing" class="gap-3" type="number" @complete="onComplete">
                <PinInputGroup v-for="index in 6" :key="index">
                    <PinInputSlot :index="index - 1" class="h-14 w-12 !text-2xl font-bold focus:ring-primary" />
                </PinInputGroup>
            </PinInput>
            <p v-if="form.errors.code" class="text-small mt-2 text-danger">
                {{ form.errors.code }}
            </p>
        </div>

        <div class="mt-6 flex justify-center">
            <jet-button :disabled="countdown > 0 || isResending" :is-processing="isResending" type="button" variant="link" @click="resendCode">
                {{ trans('profile.sms_notifications.action.resend_code') }}
                <template v-if="countdown > 0">({{ countdown }}s)</template>
            </jet-button>
        </div>

        <template #footer>
            <jet-button style="flex: 1" type="button" variant="secondary" @click="closeModal">
                {{ trans('general.action.cancel') }}
            </jet-button>
            <jet-button :disabled="codeDigits.length !== 6 || form.processing" :is-processing="form.processing" style="flex: 1" @click="verifyCode">
                {{ trans('profile.sms_notifications.action.enable') }}
            </jet-button>
        </template>
    </Modal>
</template>

<script setup>
import JetButton from '@/Jetstream/Button.vue';
import { PinInput, PinInputGroup, PinInputSlot } from '@/Components/ui/pin-input';
import { router, useForm } from '@inertiajs/vue3';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { $http } from '@/bootstrap.js';

const modalRef = ref(null);
const pinInputRef = ref(null);
const codeDigits = ref([]);
const countdown = ref(0);
const isResending = ref(false);
let countdownInterval = null;

const props = defineProps({
    phoneNumber: {
        type: String,
        required: true,
    },
    cooldown_seconds: {
        type: Number,
        default: 60,
    },
});

const form = useForm({
    phoneNumber: props.phoneNumber,
    code: '',
});

const onComplete = () => {
    verifyCode();
};

const startCountdown = () => {
    countdown.value = props.cooldown_seconds;
    clearInterval(countdownInterval);
    countdownInterval = setInterval(() => {
        if (countdown.value > 0) {
            countdown.value--;
        } else {
            clearInterval(countdownInterval);
        }
    }, 1000);
};

const resendCode = async () => {
    isResending.value = true;
    form.reset();

    try {
        await $http.post(route('user.phone.verification-code.send'), {
            phoneNumber: props.phoneNumber,
            resend: true,
        });
        startCountdown();
    } finally {
        isResending.value = false;
    }
};

const verifyCode = () => {
    form.clearErrors();

    form.transform((data) => {
        data.code = codeDigits.value.join('');
        return data;
    }).post(route('user.phone.verify'), {
        preserveScroll: true,
        onSuccess: () => {
            modalRef.value?.close();
            router.reload({ only: ['user'] });
        },
    });
};

const closeModal = () => {
    modalRef.value?.close();
};

onMounted(() => {
    nextTick(() => {
        // Focus the first input slot
        pinInputRef.value?.$el?.querySelector('input')?.focus();
    });
    startCountdown();
});

onUnmounted(() => {
    clearInterval(countdownInterval);
});
</script>
